import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import Button from '../../components/atoms/forms/Button';
import { GetLocationRoutes } from '../../libs/GraphQL';
import { applyClientFilters, formatDateForApi } from './utils';
import DayPhaseToggle from './RouteOperations/DayPhaseToggle';
import LineHaulSection, {
  EVENT_ARRIVAL,
  EVENT_DEPARTURE,
  parseLineHaulData,
} from './RouteOperations/LineHaulSection';
import HeadcountSection, {
  PHASE_PREP as HEADCOUNT_PHASE_PREP,
  PHASE_DAY_OF as HEADCOUNT_PHASE_DAY_OF,
  parseDriverHeadcountData,
  parseVanHeadcountData,
} from './RouteOperations/HeadcountSection';
import MissedStopsReviewSection from './RouteOperations/MissedStopsReviewSection';
import DispatchBar from './RouteOperations/DispatchBar';
import RouteCard from './RouteOperations/RouteCard';
import ConsolidateSelectedModal from './RouteOperations/ConsolidateSelectedModal';
import NextmvReceiptStrip, {
  RECEIPT_STATUS,
} from './RouteOperations/NextmvReceiptStrip';
import ConsolidationShelf from './RouteOperations/ConsolidationShelf';
import {
  resequenceReroute,
  removeStop,
  driverCallOff,
  missedStopsReattempt,
  unpackResponse,
  applyVehicleShiftOverride,
} from './RouteOperations/nextmvRuns';
import useConfirmReceiptsPoller from './RouteOperations/useConfirmReceiptsPoller';
import useRouteOpsHeartbeat from './RouteOperations/useRouteOpsHeartbeat';
import useReceiptsAndSyncing from './RouteOperations/useReceiptsAndSyncing';
import useStagedNextmvRun from './RouteOperations/useStagedNextmvRun';
import useDriverActionStatuses from '../../hooks/useDriverActionStatuses';
import useUserRole from '../../hooks/useUserRole';
import useSupervisorActions from './RouteOperations/useSupervisorActions';
import useSupervisorAction from './RouteOperations/useSupervisorAction';
import { cleanupStaleChecklistKeys } from './RouteOperations/useDailyChecklistDraft';
import { STATUS_IDS } from '../../libs/stopStatus';
import {
  getActiveStockingDate,
  getDayBefore,
  groupStopsByRoute,
  readHeadcountField,
  getStopRowKey,
  ACTIVE_DATE_CUTOFF_HOUR,
  PHASE_DAY_OF,
  toReviewPhase,
} from './RouteOperations/utils';
import { resolveStopWindow } from './RouteOperations/windowDisplay';
import { buildMissedStopReattemptInputs } from './RouteOperations/missedStopReattempt';
import { PRETTY_TOAST_OPTS as toastOpts } from '../../utils/toast';

// Backwards-compat for missedStopAssignments persisted before the
// auto-assign mode was introduced. Pre-existing entries always had a
// targetRouteName, so 'specific' is the right legacy default.
function normalizeAssignmentMap(map) {
  const out = {};
  Object.entries(map || {}).forEach(([k, a]) => {
    if (!a) return;
    out[k] = { ...a, mode: a.mode || 'specific' };
  });
  return out;
}

// `mode` is guaranteed to be set by normalizeAssignmentMap; the optional
// chaining is defensive against a transiently missing entry (e.g. before
// the load useEffect resolves).
function isAutoAssignment(a) {
  return (a?.mode || 'specific') === 'auto';
}

const StyledDiv = styledComponent('div', {
  '& .ro-header': {
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    padding: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.sectionGap,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing.sectionGap,
    flexWrap: 'wrap',
  },
  '& .ro-header-main': {
    flex: 1,
    minWidth: 0,
  },
  '& .ro-header-actions': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.fieldGap,
    flexShrink: 0,
  },
  '& .ro-routes-section': {
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    padding: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.sectionGap,
  },
  '& .ro-routes-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.fieldGap,
    marginBottom: theme.spacing.fieldGap,
    flexWrap: 'wrap',
  },
  '& .ro-routes-title': {
    fontSize: theme.typography.sectionTitle.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .ro-routes-callout': {
    background: theme.colors.successBg,
    borderLeft: `4px solid ${theme.colors.success}`,
    padding: theme.spacing.bodyPadding,
    borderRadius: theme.radii.input,
    marginBottom: theme.spacing.fieldGap,
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textDark,
    lineHeight: theme.lineHeights.body,
  },
  '& .ro-routes-callout-title': {
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.successText,
    marginBottom: theme.spacing.labelGap,
  },
  '& .ro-editor-lock-banner': {
    background: theme.colors.warningCalloutBg,
    border: `1px solid ${theme.colors.warningCalloutBorder}`,
    borderRadius: theme.radii.card,
    color: theme.colors.warningCalloutText,
    padding: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.fieldGap,
    fontSize: theme.typography.label.fontSize,
    lineHeight: theme.lineHeights.body,
  },
  '& .ro-editor-lock-banner strong': {
    color: theme.colors.warningCalloutAccent,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .ro-lead-banner': {
    background: theme.colors.accentSoftBg,
    border: `1px solid ${theme.colors.accent}`,
    borderRadius: theme.radii.card,
    color: theme.colors.textDark,
    padding: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.fieldGap,
    fontSize: theme.typography.label.fontSize,
    lineHeight: theme.lineHeights.body,
  },
  '& .ro-lead-banner strong': {
    color: theme.colors.accent,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .ro-routes-empty': {
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    fontSize: theme.typography.label.fontSize,
    padding: theme.spacing.bodyPadding,
    textAlign: 'center',
  },
  '& .ro-phase-note': {
    marginTop: theme.spacing.gapXl,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    lineHeight: 1.5,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.labelGap,
  },
  '& .ro-phase-note-cutoff': {
    marginTop: theme.spacing.labelGap,
    fontStyle: 'italic',
  },
  '& .ro-body': {
    // Container only — child sections own their own card chrome.
  },
  '& .ro-placeholder': {
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    padding: theme.spacing.emptyStatePadding,
    textAlign: 'center',
    color: theme.colors.textMuted,
    fontStyle: 'italic',
  },
});

function RouteOperationsTab({
  region,
  market,
  fcByLocationId,
  nameByLocationId,
  coordsByLocationId,
  refetchKey,
  onLoadingChange,
  boardRefreshKey,
  onBoardRefreshLoadingChange,
  onBoardRefreshed,
}) {
  const { isLead } = useUserRole();
  const [selectedPhase, setSelectedPhase] = useState(PHASE_DAY_OF);
  const [routes, setRoutes] = useState([]);
  const [missedStops, setMissedStops] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Per-phase Line Haul completion tracking — each section has its own
  // localStorage entry so the parent tracks them separately.
  const [dayOfLineHaulComplete, setDayOfLineHaulComplete] = useState(false);
  const [prepLineHaulComplete, setPrepLineHaulComplete] = useState(false);
  // Day Of Headcount completion is the second gate for the Dispatch bar.
  const [dayOfHeadcountComplete, setDayOfHeadcountComplete] = useState(false);
  const requestIdRef = useRef(0);
  const prevBoardRefreshKeyRef = useRef(boardRefreshKey);
  const [isRefreshingRoutes, setIsRefreshingRoutes] = useState(false);

  // Per-route consolidation selection (route name → bool), receipt strips for
  // in-flight reroutes (one per submitted route), and the set of route names
  // currently syncing (waiting for polling to confirm server state catches up
  // to the optimistic UI). All scoped to this tab; no persistence.
  const [consolidationSelection, setConsolidationSelection] = useState(
    () => new Set(),
  );
  const [consolidationModal, setConsolidationModal] = useState({
    show: false,
    source: 'manual',
  });
  const {
    receipts,
    syncingRoutes,
    addReceipt,
    patchReceipt,
    dismissReceipt,
    markRouteSyncing,
    removeFromSyncing,
  } = useReceiptsAndSyncing();

  // A reroute is in flight while any route is syncing or any receipt is
  // Processing. Drives the confirm poller, suppresses the heartbeat's
  // routes fetch, and locks the editor.
  const hasInflight =
    syncingRoutes.size > 0 ||
    receipts.some((r) => r.status === RECEIPT_STATUS.PROCESSING);

  // Whole-editor lock: a createNextmvRun regenerates the ENTIRE staged run,
  // so while any reroute is in flight every route card, consolidation entry
  // point, and missed-stops run trigger goes read-only — not just the edited
  // route. Today this only sees THIS session's in-flight work (hasInflight);
  // once the backend adds run status to getStagedNextmvRun, OR in a
  // stagedRunPending flag so another supervisor's run locks this tab too.
  const isEditorLocked = hasInflight;

  // Missed-stop assignments staged from yesterday's Missed Stops Review.
  // Shape: { [rowKey]: { targetRouteName, stopId, originalRoute,
  //                       originalDate, toteCountPerStop } }
  // Single source of truth here so ghost rows can render on the Day Of
  // route editor regardless of which phase tab the supervisor is on.
  // MissedStopsReviewSection (only mounted on Prep) syncs in via callback;
  // we also hydrate from localStorage on mount so the Day Of tab works
  // even if Prep hasn't been opened this session.
  const [missedStopAssignments, setMissedStopAssignments] = useState({});
  // Forces MissedStopsReviewSection to remount (clearing its draft) when
  // the route editor's Cancel button wipes an assignment externally.
  const [missedStopsRemountKey, setMissedStopsRemountKey] = useState(0);

  // Single "active stocking date" — both chips operate on this date for
  // the routes fetch. The Yesterday chip ALSO fetches the day-before's
  // missed stops list. Active date follows the 4pm-cutoff rule.
  const activeDate = useMemo(() => getActiveStockingDate(), []);
  const priorDate = useMemo(() => getDayBefore(activeDate), [activeDate]);
  const activeDateStr = useMemo(
    () => formatDateForApi(activeDate),
    [activeDate],
  );
  const priorDateStr = useMemo(() => formatDateForApi(priorDate), [priorDate]);

  // Once per tab mount, drop checklist drafts older than the retention window.
  // Owned here (not in the per-section useDailyChecklistDraft hook) so the
  // sweep runs a single time regardless of how many sections mount.
  useEffect(() => {
    cleanupStaleChecklistKeys();
  }, []);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;

    const isRefresh = boardRefreshKey !== prevBoardRefreshKeyRef.current;
    prevBoardRefreshKeyRef.current = boardRefreshKey;
    if (isRefresh) {
      setIsRefreshingRoutes(true);
    } else {
      setIsLoading(true);
    }

    // Routes for the active date — needed by both chips (execution flow
    // on Today; the route picker for Missed Stops Review on Yesterday).
    // calculateTotes: true is required for toteCountPerStop / toteCountPerRoute
    // to be populated (BE resolver gates the field on this flag). The flag is
    // ignored when missedStopsOnly: true, so it stays off the missed-stops
    // fetch below.
    const routesPromise = GetLocationRoutes({
      stockingDate: activeDateStr,
      calculateTotes: true,
    });
    // Day-before missed stops — fetched in both phases so the Missed Stops
    // Review + reopt queue work on both the Prep and Day-Of tabs.
    const missedStopsPromise = GetLocationRoutes({
      stockingDate: priorDateStr,
      missedStopsOnly: true,
    });

    Promise.all([routesPromise, missedStopsPromise])
      .then(([routesResp, missedStopsResp]) => {
        if (cancelled || requestIdRef.current !== requestId) return;
        setRoutes(routesResp?.routes || []);
        setMissedStops(missedStopsResp?.routes || []);
      })
      .catch((e) => {
        if (cancelled || requestIdRef.current !== requestId) return;
        console.error('RouteOperationsTab fetch failed:', e);
        toast.error(
          `Failed fetching data for ${activeDateStr}. Please try again.`,
          toastOpts,
        );
        setRoutes([]);
        setMissedStops([]);
      })
      .finally(() => {
        if (cancelled || requestIdRef.current !== requestId) return;
        setIsLoading(false);
        setIsRefreshingRoutes(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPhase, activeDate, priorDate, refetchKey, boardRefreshKey]);

  // Bubble loading state up to the parent (the FilterBar uses this to
  // render the "Applying…" state on the Apply button). The cleanup resets the
  // parent on unmount — without it, switching tabs mid-fetch strands the
  // parent's flag at true and the Apply button locks up forever (SCT-760).
  useEffect(() => {
    onLoadingChange(isLoading);
    return () => {
      onLoadingChange(false);
    };
  }, [isLoading, onLoadingChange]);

  // Driver-action statuses fan-out. Fetched against both the active stocking
  // date and the day before so the 6pm-Chicago overlap window catches late
  // events. `refresh()` is invoked when the page-level button increments
  // `statusesRefreshKey`. Status fan-out is independent of `refetchKey`
  // (Apply Filters) because the events depend on date + market, not the
  // route projection — and we don't want every Apply click to invalidate
  // the (potentially large) bucketed cache.
  const statusDates = useMemo(
    () => [activeDateStr, priorDateStr],
    [activeDateStr, priorDateStr],
  );
  const marketValue = region?.value;
  const {
    statusesByDate,
    isLoading: isLoadingStatuses,
    refresh: refreshStatuses,
  } = useDriverActionStatuses({
    dates: statusDates,
    routes,
    marketValue,
  });

  // Supervisor actions — newest action per (reviewPhase, eventType) keyed by
  // `${phase}__${eventType}`. Used to hydrate the Line Haul + Headcount
  // section forms with previously saved server state.
  const {
    actionsByKey: supervisorActionsByKey,
    isLoading: isLoadingSupervisorActions,
    refresh: refreshSupervisorActions,
  } = useSupervisorActions({
    reviewDate: activeDateStr,
  });

  useEffect(() => {
    if (boardRefreshKey > 0) {
      refreshStatuses();
      refreshSupervisorActions();
    }
    // The refresh fns are identity-stable per their hook contracts, so we
    // intentionally depend only on boardRefreshKey — this effect should re-run
    // when the key bumps, not on fn identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardRefreshKey]);

  const isBoardRefreshing =
    isLoadingStatuses || isLoadingSupervisorActions || isRefreshingRoutes;

  useEffect(() => {
    onBoardRefreshLoadingChange(isBoardRefreshing);
    if (!isBoardRefreshing) {
      onBoardRefreshed(new Date());
    }
  }, [isBoardRefreshing, onBoardRefreshLoadingChange, onBoardRefreshed]);

  // Enrich routes with FC name (the locationRoutes query doesn't populate
  // it directly) and driver-action derivedStatus. Then Region + Market
  // client-side filters apply.
  //
  // Delivery windows are NOT derived here — resolveStopWindow (see
  // RouteOperations/windowDisplay.js) merges the raw layers per stop in
  // stopsWithDisplayName below; nextmvDeliveryWindowStart/End and
  // calculatedStartTime/End pass through this memo untouched.
  // `isOpen24h` is not derived either: empty nextmv fields can't be told
  // apart from a pre-deploy row, so the 24h pill stays dormant until BE
  // provides a reliable signal. StopRow keeps the rendering.
  const enrichedRoutes = useMemo(() => {
    const statusesForActive = statusesByDate[activeDateStr];
    return routes.map((route) => ({
      ...route,
      fulfillmentCenter:
        fcByLocationId[route.id] || route.fulfillmentCenter || '',
      derivedStatus: (statusesForActive && statusesForActive.get(route.id)) || {
        status: STATUS_IDS.NOT_STARTED,
      },
    }));
  }, [routes, fcByLocationId, statusesByDate, activeDateStr]);

  const filteredRoutes = useMemo(
    () => applyClientFilters(enrichedRoutes, { region, market }),
    [enrichedRoutes, region, market],
  );

  // Grouped + sequenced view used by both the JSX (route cards) and the
  // callbacks below (route lookup in mutation handlers). Memoized once
  // here so consumers don't redundantly recompute groupStopsByRoute.
  const enrichedRouteGroups = useMemo(
    () => groupStopsByRoute(filteredRoutes),
    [filteredRoutes],
  );

  // The GraphQL response is flat — one record per stop — so filteredRoutes.length
  // counts stops, not routes. Group by `route` field to get the distinct route
  // count for the Headcount section's plan verification tiles.
  const distinctRouteCount = useMemo(
    () => enrichedRouteGroups.length,
    [enrichedRouteGroups],
  );

  // Apply the same client-side Region/Market filter to yesterday's missed
  // stops so the Prep view respects the global filter. Enrich first with the
  // FC lookup, matching the routes pipeline above.
  const filteredMissedStops = useMemo(() => {
    const enriched = missedStops.map((stop) => ({
      ...stop,
      fulfillmentCenter:
        fcByLocationId[stop.id] || stop.fulfillmentCenter || '',
    }));
    return applyClientFilters(enriched, { region, market });
  }, [missedStops, fcByLocationId, region, market]);

  // Full missed-stop records keyed by the same rowKey the reopt queue uses, so
  // handleReoptimize can recover stockingIndex + delivery windows (the thin
  // queue entries don't carry them).
  const missedStopsByRowKey = useMemo(() => {
    const map = {};
    filteredMissedStops.forEach((stop) => {
      map[getStopRowKey(stop)] = stop;
    });
    return map;
  }, [filteredMissedStops]);

  // The Headcount shortage CTA passes up the union of driver+van missing-
  // routes the supervisor checked in the multi-selects, so we can pre-check
  // those routes' consolidation checkboxes before opening the review modal.
  const handleConsolidateFromHeadcount = useCallback(
    ({ unavailableRoutes = [] } = {}) => {
      if (isEditorLocked) return;
      if (unavailableRoutes.length > 0) {
        setConsolidationSelection(new Set(unavailableRoutes));
      }
      setConsolidationModal({ show: true, source: 'headcount' });
    },
    [isEditorLocked],
  );

  const handleConsolidateFromHeader = useCallback(() => {
    if (isEditorLocked) return;
    setConsolidationModal({ show: true, source: 'manual' });
  }, [isEditorLocked]);

  const toggleConsolidationSelection = useCallback((routeName, checked) => {
    setConsolidationSelection((prev) => {
      const next = new Set(prev);
      if (checked) next.add(routeName);
      else next.delete(routeName);
      return next;
    });
  }, []);

  // --- Missed-stop assignment plumbing ---

  // Hydrate the assignment map from the same localStorage key
  // MissedStopsReviewSection writes to (via useDailyChecklistDraft). Lets the
  // Day Of view render ghost rows even before the Prep tab is opened in
  // this session.
  useEffect(() => {
    if (!activeDateStr) return;
    const key = `route-ops:checklist:${activeDateStr}:missed-stops`;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        setMissedStopAssignments({});
        return;
      }
      const parsed = JSON.parse(raw);
      setMissedStopAssignments(
        normalizeAssignmentMap(parsed?.fields?.assignments || {}),
      );
    } catch (e) {
      setMissedStopAssignments({});
    }
  }, [activeDateStr]);

  // Receive updates from MissedStopsReviewSection (Prep tab only) so the
  // assignment map stays in sync without a localStorage poll.
  const handleMissedStopAssignmentsChange = useCallback((assignments) => {
    setMissedStopAssignments(normalizeAssignmentMap(assignments || {}));
  }, []);

  // Wired into MissedStopCard's "Go to Route Editor →" button. Scrolls the
  // target RouteCard into view and runs the focus-ring flash keyframe so
  // the supervisor's eye lands on the staged ghost row. Finds the card via
  // its existing data-testid rather than threading a ref through every
  // RouteCard render.
  const scrollToRoute = useCallback((routeName) => {
    const selector = `[data-testid="route-card-${routeName}"]`;
    const el = document.querySelector(selector);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('is-focus-flash');
    window.setTimeout(() => {
      el.classList.remove('is-focus-flash');
    }, 2000);
  }, []);

  // Called when a supervisor hits Cancel on a route card whose ghost row is
  // present. Removes that route's assignments, writes the cleaned-up map
  // back to localStorage (so Prep's draft stays in sync), and bumps the
  // remount key so the Missed Stops Review section drops its in-memory
  // copy on re-mount.
  const clearMissedStopsForRoute = useCallback(
    (routeName) => {
      const next = {};
      Object.entries(missedStopAssignments).forEach(([k, a]) => {
        if (!a) return;
        // Auto-mode entries don't belong to any route — Cancel on a route
        // card shouldn't touch them.
        if (isAutoAssignment(a)) {
          next[k] = a;
          return;
        }
        if (a.targetRouteName !== routeName) next[k] = a;
      });
      setMissedStopAssignments(next);
      const key = `route-ops:checklist:${activeDateStr}:missed-stops`;
      try {
        const raw = window.localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : { fields: {}, completed: false };
        parsed.fields = parsed.fields || {};
        parsed.fields.assignments = next;
        parsed.updatedAt = new Date().toISOString();
        window.localStorage.setItem(key, JSON.stringify(parsed));
      } catch (e) {
        // Non-fatal — in-memory state is still correct; the on-disk copy
        // will get rewritten on the next Missed Stops Review edit.
      }
      setMissedStopsRemountKey((k) => k + 1);
    },
    [missedStopAssignments, activeDateStr],
  );

  // Derive the auto-assign queue from the same map that drives ghost rows.
  // The shape feeds buildMissedStopReattemptInputs in handleReoptimize.
  const reoptEntries = useMemo(
    () =>
      Object.entries(missedStopAssignments)
        .filter(([, a]) => a && isAutoAssignment(a))
        .map(([rowKey, a]) => ({
          rowKey,
          stopId: a.stopId || '',
          locationName: nameByLocationId[a.stopId] || a.stopId || rowKey,
          originalRoute: a.originalRoute || '',
          originalDate: a.originalDate || '',
          toteCountPerStop:
            typeof a.toteCountPerStop === 'number' ? a.toteCountPerStop : null,
        })),
    [missedStopAssignments, nameByLocationId],
  );

  // Staged Nextmv run for the current (date, fc) — vehicles + previousRunId
  // sourced from the dedicated hook below. A missing/failed staged run blocks
  // rerouting via the handler guards (see handleRouteUpdateConfirmed /
  // handleConsolidationSubmit).
  //
  // For now there's a single visible fc per filter view, so we read it
  // from the first route. Once multi-fc views exist this becomes a Map
  // keyed by fc.
  const activeFc = useMemo(() => {
    const firstRoute = filteredRoutes[0];
    return firstRoute ? firstRoute.fulfillmentCenter || '' : '';
  }, [filteredRoutes]);

  // Append-only audit trail for route edits (same table the checklist
  // sections write to). Currently emits one RemoveStop event per stop the
  // supervisor removes via Update Route, carrying the modal's reason.
  const { submit: submitRemoveStopAudit } = useSupervisorAction({
    fulfillmentCenter: activeFc,
    market: marketValue,
    reviewDate: activeDateStr,
    reviewPhase: toReviewPhase(selectedPhase),
  });

  // Bumped by the confirm poller when a reroute lands, so the staged run
  // re-fetches and `previousRunId` advances to the just-applied run.
  const [stagedRefreshKey, setStagedRefreshKey] = useState(0);
  // Heartbeat + confirm poller both bump this; memoized so the heartbeat's
  // tick callback stays identity-stable.
  const refreshStagedRun = useCallback(
    () => setStagedRefreshKey((k) => k + 1),
    [],
  );
  const {
    vehicles: stagedVehicles,
    previousRunId: stagedPreviousRunId,
    error: stagedRunError,
  } = useStagedNextmvRun(activeDateStr, activeFc, stagedRefreshKey);

  // Staged-run vehicles are FC-wide; a vehicle's id equals its route name, so
  // match each to its route. locationRoutes carries no per-route fleet data, so
  // this is where the vehicle header, capacity, and draft-staleness tag come
  // from until the query exposes them.
  const stagedVehicleByRoute = useMemo(() => {
    const byRoute = new Map();
    stagedVehicles.forEach((v) => {
      if (v && v.id) byRoute.set(v.id, v);
    });
    return byRoute;
  }, [stagedVehicles]);

  const buildMutationContext = useCallback(
    (route) => ({
      dropOffDate: formatDateForApi(activeDate),
      fulfillmentCenter: route.fulfillmentCenter || activeFc,
      vehicles: stagedVehicles,
      previousRunId: stagedPreviousRunId,
    }),
    [activeDate, activeFc, stagedVehicles, stagedPreviousRunId],
  );

  // Fires a MissedStopsReattempt run for the auto-assign queue. Optimistic:
  // we add a receipt (Processing) immediately, mirroring the Update Route
  // flow's receipt pattern. Blocks (with a toast, no partial run) when no
  // staged run exists yet, or when any queued stop can't be resolved to a
  // full input (missing record / coords / delivery window) — partial runs
  // would silently drop stops the supervisor thinks are queued.
  const handleReoptimize = useCallback(async () => {
    if (isEditorLocked) return;
    if (reoptEntries.length === 0) return;

    if (stagedRunError || !stagedPreviousRunId) {
      toast.error(
        'No optimized run exists for this date yet — generate one on the NextMV page before rerouting.',
        toastOpts,
      );
      return;
    }

    const { missedStops: reattemptStops, skipped } =
      buildMissedStopReattemptInputs({
        queueEntries: reoptEntries,
        missedStopsByRowKey,
        coordsByLocationId,
        dropOffDate: formatDateForApi(activeDate),
      });

    if (skipped.length > 0) {
      const MAX_NAMED = 8;
      const names = skipped
        .slice(0, MAX_NAMED)
        .map((s) => s.locationName)
        .join(', ');
      const extra =
        skipped.length > MAX_NAMED
          ? ` and ${skipped.length - MAX_NAMED} more`
          : '';
      toast.error(
        `Can't re-optimize — missing location data for ${skipped.length} stop${
          skipped.length === 1 ? '' : 's'
        }: ${names}${extra}. Refresh and try again.`,
        toastOpts,
      );
      return;
    }
    if (reattemptStops.length === 0) return;

    const context = buildMutationContext({});
    const receiptId = `missed-stops-reattempt-${Date.now()}`;
    addReceipt({
      id: receiptId,
      routeName: 'Missed stops re-attempt',
      runId: '',
      status: RECEIPT_STATUS.SUBMITTED,
      errorMessage: '',
    });

    try {
      patchReceipt(receiptId, { status: RECEIPT_STATUS.PROCESSING });
      const response = await missedStopsReattempt({
        context,
        missedStops: reattemptStops,
      });
      const { ok, runId, message } = unpackResponse(response);
      if (!ok) {
        patchReceipt(receiptId, {
          runId,
          status: RECEIPT_STATUS.FAILED,
          errorMessage: message || 'Re-optimization failed',
        });
        return;
      }
      patchReceipt(receiptId, { runId, status: RECEIPT_STATUS.PROCESSING });
      setMissedStopAssignments((prev) => {
        const next = {};
        Object.entries(prev).forEach(([k, a]) => {
          if (a && !isAutoAssignment(a)) next[k] = a;
        });
        return next;
      });
    } catch (err) {
      console.error('Missed stops reattempt mutation failed:', err);
      patchReceipt(receiptId, {
        status: RECEIPT_STATUS.FAILED,
        errorMessage: err.message || 'Network error',
      });
    }
  }, [
    isEditorLocked,
    reoptEntries,
    stagedRunError,
    stagedPreviousRunId,
    missedStopsByRowKey,
    coordsByLocationId,
    activeDate,
    buildMutationContext,
    addReceipt,
    patchReceipt,
  ]);

  // Fires the right createNextmvRun for each part of the supervisor's edits.
  // Optimistic: we add a receipt (Processing) immediately and mark the route
  // as syncing. The polling loop below will flip receipts to Confirmed when
  // a fresh routes fetch reflects the new state.
  const handleRouteUpdateConfirmed = useCallback(
    async ({ routeName, payload }) => {
      if (stagedRunError || !stagedPreviousRunId) {
        toast.error(
          'No optimized run exists for this date yet — generate one on the NextMV page before rerouting.',
          toastOpts,
        );
        return;
      }
      const route =
        enrichedRouteGroups.find((r) => r.routeName === routeName)?.stops[0] ||
        {};
      const baseContext = buildMutationContext(route);
      let context;
      try {
        context = {
          ...baseContext,
          vehicles: applyVehicleShiftOverride(
            baseContext.vehicles,
            payload.vehicleOverride,
          ),
        };
      } catch (err) {
        toast.error(
          'Could not apply the shift change — the assigned vehicle is no longer on this route. Refresh and try again.',
          toastOpts,
        );
        return;
      }
      markRouteSyncing(routeName, true);

      const receiptId = `${routeName}-${Date.now()}`;
      addReceipt({
        id: receiptId,
        routeName,
        runId: '',
        status: RECEIPT_STATUS.SUBMITTED,
        errorMessage: '',
      });

      const hasResequence =
        payload.resequenceStops && payload.resequenceStops.length > 0;
      const hasRemove = payload.removedStops && payload.removedStops.length > 0;
      // A shift-only change (no stop edits) still needs one reroute so NextMV
      // re-optimizes under the new shift window. Combined edits let the
      // overridden vehicles ride along on whichever calls already fire.
      const shiftOnly = payload.vehicleOverride && !hasResequence && !hasRemove;

      const calls = [];
      if (hasResequence || shiftOnly) {
        calls.push(
          resequenceReroute({
            context,
            resequenceStops: payload.resequenceStops || [],
          }),
        );
      }
      if (hasRemove) {
        calls.push(removeStop({ context, removedStops: payload.removedStops }));
      }

      try {
        patchReceipt(receiptId, { status: RECEIPT_STATUS.PROCESSING });
        const results = await Promise.all(calls);
        const unpacked = results.map(unpackResponse);
        const firstFail = unpacked.find((u) => !u.ok);
        const runId = unpacked.find((u) => u.runId)?.runId || '';
        if (firstFail) {
          patchReceipt(receiptId, {
            runId,
            status: RECEIPT_STATUS.FAILED,
            errorMessage: firstFail.message || 'Reroute failed',
          });
          markRouteSyncing(routeName, false);
        } else {
          // Hold at Processing — the polling loop will flip to Confirmed
          // once the server state catches up. We attach the runId now so
          // the receipt shows it during processing.
          patchReceipt(receiptId, { runId, status: RECEIPT_STATUS.PROCESSING });
          // Audit trail: one RemoveStop SupervisorAction per removed stop,
          // carrying the reason captured in the remove modal. Only on a
          // successful submit — a failed reroute removed nothing.
          (payload.removedStops || []).forEach((stopId) => {
            const reasonEntry =
              (payload.removeReasonsByStopId || {})[stopId] || {};
            submitRemoveStopAudit({
              eventType: 'RemoveStop',
              data: {
                stopId,
                locationId: reasonEntry.locationId || '',
                routeName,
                nextmvRunId: runId,
                reason: reasonEntry.reason || '',
                notes: reasonEntry.notes || '',
              },
            });
          });
        }
      } catch (err) {
        console.error('Route update mutation failed:', err);
        patchReceipt(receiptId, {
          status: RECEIPT_STATUS.FAILED,
          errorMessage: err.message || 'Network error',
        });
        markRouteSyncing(routeName, false);
      }
    },
    [
      buildMutationContext,
      enrichedRouteGroups,
      markRouteSyncing,
      addReceipt,
      patchReceipt,
      stagedRunError,
      stagedPreviousRunId,
      submitRemoveStopAudit,
    ],
  );

  // Resolve the supervisor-reported driver / van counts when the
  // consolidation modal opens. The Headcount section persists a draft under
  // `route-ops:checklist:<date>:headcount-day-of`; if the supervisor said
  // "yes — all available" we fall back to the planned route count (1
  // driver/van per planned route). Drivers and vans are read independently
  // so a supervisor who reported 50 drivers / 55 vans sees 50/55 here
  // rather than collapsing both to the same number.
  const consolidationAvailability = useMemo(() => {
    if (!consolidationModal.show) {
      return { availableDrivers: 0, availableVans: 0 };
    }
    const driversAvailable = readHeadcountField(
      activeDateStr,
      'driversAvailable',
    );
    const actualDriverCount = readHeadcountField(
      activeDateStr,
      'actualDriverCount',
    );
    const vansAvailable = readHeadcountField(activeDateStr, 'vansAvailable');
    const actualVanCount = readHeadcountField(activeDateStr, 'actualVanCount');
    const resolve = (statusValue, countValue) => {
      if (statusValue === 'no' && countValue !== '') {
        const n = Number(countValue);
        return Number.isFinite(n) && n >= 0 ? n : distinctRouteCount;
      }
      return distinctRouteCount;
    };
    return {
      availableDrivers: resolve(driversAvailable, actualDriverCount),
      availableVans: resolve(vansAvailable, actualVanCount),
    };
  }, [consolidationModal.show, activeDateStr, distinctRouteCount]);

  const handleConsolidationSubmit = useCallback(
    async ({ selectedRouteNames }) => {
      if (isEditorLocked) {
        setConsolidationModal({ show: false, source: 'manual' });
        setConsolidationSelection(new Set());
        return;
      }
      if (stagedRunError || !stagedPreviousRunId) {
        toast.error(
          'No optimized run exists for this date yet — generate one on the NextMV page before rerouting.',
          toastOpts,
        );
        return;
      }
      const groups = enrichedRouteGroups.filter((g) =>
        selectedRouteNames.includes(g.routeName),
      );
      // A route is served by exactly one vehicle/driver and is identified by
      // its route id, so the missing vehicles for a DriverCallOff are just the
      // ids of the routes being consolidated — their driver/vehicle is gone.
      const missingVehicles = groups.map((g) => g.routeName).filter(Boolean);
      const firstRouteStop = groups[0]?.stops[0] || {};
      const context = buildMutationContext(firstRouteStop);

      const receiptId = `consolidate-${Date.now()}`;
      addReceipt({
        id: receiptId,
        routeName: selectedRouteNames.join(', '),
        runId: '',
        status: RECEIPT_STATUS.SUBMITTED,
        errorMessage: '',
      });

      try {
        const response = await driverCallOff({
          context,
          missingVehicles,
        });
        const { ok, runId, message } = unpackResponse(response);
        patchReceipt(receiptId, {
          runId,
          status: ok ? RECEIPT_STATUS.PROCESSING : RECEIPT_STATUS.FAILED,
          errorMessage: ok ? '' : message || 'Consolidation failed',
        });
      } catch (err) {
        console.error('Consolidation mutation failed:', err);
        patchReceipt(receiptId, {
          status: RECEIPT_STATUS.FAILED,
          errorMessage: err.message || 'Network error',
        });
      } finally {
        setConsolidationModal({
          show: false,
          source: 'manual',
        });
        setConsolidationSelection(new Set());
      }
    },
    [
      buildMutationContext,
      enrichedRouteGroups,
      addReceipt,
      patchReceipt,
      stagedRunError,
      stagedPreviousRunId,
      isEditorLocked,
    ],
  );

  // Polling: while any route is syncing or any receipt is in Processing,
  // refetch routes every 30s. Completion is detected off the staged run —
  // a Processing receipt confirms when getStagedNextmvRun.nextmvRunId equals
  // the receipt's runId; a receipt that never flips (a failed run, or a no-op
  // submit) times out to Failed so the card can't lock forever.
  useConfirmReceiptsPoller({
    enabled: hasInflight,
    activeDate,
    fulfillmentCenter: activeFc,
    receipts,
    syncingRoutes,
    patchReceipt,
    removeFromSyncing,
    onRoutesFetched: setRoutes,
    onConfirmed: () => setStagedRefreshKey((k) => k + 1),
  });

  // Background heartbeat — refreshes routes, driver-action statuses, the
  // staged Nextmv run, and supervisor actions every 60s so the supervisor
  // sees fresh data (including another supervisor's edits) without clicking
  // "Apply Filters" or "Refresh statuses". Delivery windows ride along with
  // the routes fetch. Routes fetch is suppressed while `hasInflight` is true
  // because the confirm poller is already covering that surface at its
  // tighter 30s cadence; the other refreshes fire on every tick.
  useRouteOpsHeartbeat({
    enabled: true,
    hasInflight,
    activeDate,
    onRoutesFetched: setRoutes,
    refreshStatuses,
    refreshStagedRun,
    refreshSupervisorActions,
  });

  // The LineHaul / MissedStops "Go to Route Editor" CTAs now scroll to the
  // routes section on the same page instead of opening a placeholder toast.
  const routesSectionRef = useRef(null);
  const scrollToRoutes = useCallback(() => {
    if (routesSectionRef.current) {
      routesSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Per-section server field hydration — parsed from the supervisor-actions
  // map so the Line Haul and Headcount forms can pre-fill from the newest
  // saved action for the current (date, phase, eventType).
  const dayOfLineHaulServer = useMemo(
    () => parseLineHaulData(supervisorActionsByKey.day_of__LineHaulArrival),
    [supervisorActionsByKey],
  );
  const prepLineHaulServer = useMemo(
    () => parseLineHaulData(supervisorActionsByKey.prep__LineHaulArrival),
    [supervisorActionsByKey],
  );
  const buildHeadcountServer = useCallback(
    (phaseKey) => {
      const driver = parseDriverHeadcountData(
        supervisorActionsByKey[`${phaseKey}__DriverHeadcount`],
      );
      const van = parseVanHeadcountData(
        supervisorActionsByKey[`${phaseKey}__VanHeadcount`],
      );
      if (!driver && !van) return null;
      return { ...(driver || {}), ...(van || {}) };
    },
    [supervisorActionsByKey],
  );
  const dayOfHeadcountServer = useMemo(
    () => buildHeadcountServer('day_of'),
    [buildHeadcountServer],
  );
  const prepHeadcountServer = useMemo(
    () => buildHeadcountServer('prep'),
    [buildHeadcountServer],
  );

  // The routes section + receipts strip render on both phases. On Day Of
  // the supervisor is dispatching; on Prep they're preparing tomorrow's
  // routes. Edits and the consolidation flow behave identically — only the
  // section title's date shifts.
  const routesTitle = `Routes for ${activeDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })} (${enrichedRouteGroups.length})`;

  let consolidateTooltip = '';
  if (isEditorLocked) {
    consolidateTooltip =
      'Routes are re-optimizing — wait for the current run to finish';
  } else if (consolidationSelection.size < 1) {
    consolidateTooltip = 'Check one or more route cards to enable';
  }

  const routesSection = (
    <>
      <div
        className="ro-routes-section"
        ref={routesSectionRef}
        data-testid="ro-routes-section"
      >
        {isLead && (
          <div className="ro-lead-banner" data-testid="ro-lead-banner">
            <strong>Lead view.</strong> Editing controls are hidden. You can
            confirm line haul, headcount, van count, and missed-stop routing.
          </div>
        )}
        <div className="ro-routes-header">
          <div className="ro-routes-title">{routesTitle}</div>
          {!isLead && (
            <Button
              variant="consolidate"
              onClick={handleConsolidateFromHeader}
              disabled={isEditorLocked || consolidationSelection.size < 1}
              dataTestId="ro-consolidate-selected-btn"
              title={consolidateTooltip}
            >
              🔀 Consolidate Selected ({consolidationSelection.size})
            </Button>
          )}
        </div>
        {isEditorLocked && (
          <div
            className="ro-editor-lock-banner"
            data-testid="ro-editor-lock-banner"
          >
            <strong>Routes are re-optimizing</strong> — editing is locked until
            the new run finishes. This usually takes a couple of minutes.
          </div>
        )}
        <div className="ro-routes-callout" data-testid="ro-routes-callout">
          <div className="ro-routes-callout-title">Route Editor</div>
          Adjust stops, edit delivery windows, or use Delete on any stop. Select
          multiple routes (checkbox in each route header) and click{' '}
          <strong>Consolidate Selected</strong> for batch NextMV reroutes.
        </div>
        {enrichedRouteGroups.length === 0 ? (
          <div className="ro-routes-empty">
            {isLoading
              ? 'Loading routes…'
              : 'No routes for the selected filters.'}
          </div>
        ) : (
          enrichedRouteGroups.map((group) => {
            const firstStop = group.stops[0] || {};
            const stopsWithDisplayName = group.stops.map((s) => ({
              ...s,
              displayName: nameByLocationId[s.id] || '',
              coords: coordsByLocationId[s.id] || null,
              // resolveStopWindow merges the window layers (supervisor
              // PinStop edit → NextMV input window → calculated stopgap)
              // into browser-local "HH:MM" strings (startTimeWindow /
              // endTimeWindow — modal prefill + sequence sorting) and
              // "Jul 2, 13:00" display strings (startTimeWindowDisplay /
              // endTimeWindowDisplay — StopRow window cells).
              ...resolveStopWindow(s),
            }));
            // Ghost rows for any missed stops the supervisor staged onto
            // THIS route via the Missed Stops Review section. NextMV will
            // assign the real sequence position when the route re-optimizes;
            // until then they appear at the bottom of the list with a `?`
            // badge and the cream/amber pending styling.
            // TODO(SCT-635): `useRouteEdits.serialize()` doesn't include
            // these in the NextMV payload yet — separate ticket.
            const ghostStops = Object.entries(missedStopAssignments)
              .filter(
                ([, a]) =>
                  a &&
                  !isAutoAssignment(a) &&
                  a.targetRouteName === group.routeName,
              )
              .map(([rowKey, a]) => ({
                id: a.stopId,
                stop: '?',
                stockingIndex: 9999,
                stockingDate: activeDateStr,
                toteCountPerStop:
                  typeof a.toteCountPerStop === 'number'
                    ? a.toteCountPerStop
                    : null,
                displayName: nameByLocationId[a.stopId] || '',
                coords: coordsByLocationId[a.stopId] || null,
                isMissedStopPending: true,
                missedStopRowKey: rowKey,
                missedStopOriginalRoute: a.originalRoute || '',
                missedStopOriginalDate: a.originalDate || '',
                derivedStatus: { status: STATUS_IDS.MISSED_STOP_PENDING },
              }));
            const routeVehicle =
              stagedVehicleByRoute.get(group.routeName) || null;
            const routeData = {
              routeName: group.routeName,
              stops: [...stopsWithDisplayName, ...ghostStops],
              fulfillmentCenter: firstStop.fulfillmentCenter || '',
              toteCountPerRoute: firstStop.toteCountPerRoute,
              capacity: routeVehicle
                ? routeVehicle.capacity
                : firstStop.capacity,
              vehicles: routeVehicle ? [routeVehicle] : [],
              previousRunId: stagedPreviousRunId,
            };
            return (
              <RouteCard
                key={group.routeName}
                route={routeData}
                dropOffDate={formatDateForApi(activeDate)}
                isSelectedForConsolidation={consolidationSelection.has(
                  group.routeName,
                )}
                isSyncing={syncingRoutes.has(group.routeName)}
                isEditorLocked={isEditorLocked}
                isLead={isLead}
                onToggleConsolidationSelection={(checked) =>
                  toggleConsolidationSelection(group.routeName, checked)
                }
                onUpdateConfirmed={handleRouteUpdateConfirmed}
                onClearMissedStopsForRoute={clearMissedStopsForRoute}
              />
            );
          })
        )}
      </div>
      {receipts.length > 0 && (
        <div data-testid="ro-receipts">
          {receipts.map((r) => (
            <NextmvReceiptStrip
              key={r.id}
              status={r.status}
              routeName={r.routeName}
              runId={r.runId}
              errorMessage={r.errorMessage}
              onDismiss={() => dismissReceipt(r.id)}
            />
          ))}
        </div>
      )}
    </>
  );

  const missedStopsReviewSection = (
    <MissedStopsReviewSection
      key={missedStopsRemountKey}
      stockingDate={formatDateForApi(activeDate)}
      missedStops={filteredMissedStops}
      todayRoutes={filteredRoutes}
      isLoading={isLoading}
      stepNumber={3}
      fulfillmentCenter={activeFc}
      market={marketValue}
      reviewPhase={toReviewPhase(selectedPhase)}
      onCompletedChange={() => {}}
      onAssignmentsChange={handleMissedStopAssignmentsChange}
      onGoToRoute={scrollToRoute}
      onRouteMissedStops={handleReoptimize}
      isEditorLocked={isEditorLocked}
      editorLockedTooltip="Routes are re-optimizing — wait for the current run to finish"
      allowSpecificAssignment={!isLead}
    />
  );

  return (
    <StyledDiv data-route-count={filteredRoutes.length}>
      <div className="ro-header">
        <div className="ro-header-main">
          <DayPhaseToggle
            selectedPhase={selectedPhase}
            onChange={setSelectedPhase}
            dayOfDate={activeDate}
          />
          <div className="ro-phase-note" data-testid="ro-phase-note">
            <div>
              <strong>Prep</strong> <em>(day before stocking)</em>: confirm Line
              Haul <strong>departure</strong>, capture any known driver/van
              shortages, roll yesterday&apos;s missed stops onto Day Of routes.
            </div>
            <div>
              <strong>Day Of</strong> <em>(day of stocking)</em>: confirm Line
              Haul <strong>arrival</strong>, lock in final headcount, dispatch
              routes.
            </div>
            <div className="ro-phase-note-cutoff">
              The Day Of date rolls forward at{' '}
              {ACTIVE_DATE_CUTOFF_HOUR % 12 || 12}
              {ACTIVE_DATE_CUTOFF_HOUR >= 12 ? ' pm' : ' am'} local time —
              that&apos;s when Prep starts for the next stocking day.
            </div>
          </div>
        </div>
        {selectedPhase === PHASE_DAY_OF && (
          <div className="ro-header-actions">
            <DispatchBar
              stockingDate={formatDateForApi(activeDate)}
              enabled={dayOfLineHaulComplete && dayOfHeadcountComplete}
            />
          </div>
        )}
      </div>
      <div className="ro-body" data-testid="route-operations-body">
        {selectedPhase === PHASE_DAY_OF ? (
          <>
            <LineHaulSection
              event={EVENT_ARRIVAL}
              stockingDate={formatDateForApi(activeDate)}
              fulfillmentCenter={activeFc}
              market={marketValue}
              stepNumber={1}
              serverFields={dayOfLineHaulServer}
              hasServerAction={Boolean(dayOfLineHaulServer)}
              onConsolidateRoutes={scrollToRoutes}
              onCompletedChange={setDayOfLineHaulComplete}
            />
            <HeadcountSection
              phase={HEADCOUNT_PHASE_DAY_OF}
              stockingDate={formatDateForApi(activeDate)}
              fulfillmentCenter={activeFc}
              market={marketValue}
              routesPlanned={distinctRouteCount}
              stopsCount={filteredRoutes.length}
              routeNames={enrichedRouteGroups.map((g) => g.routeName)}
              stepNumber={2}
              disabled={!dayOfLineHaulComplete}
              disabledTooltip="Complete Line Haul Arrival first"
              serverFields={dayOfHeadcountServer}
              hasServerAction={Boolean(dayOfHeadcountServer)}
              showConsolidateCta={!isLead}
              onConsolidateRoutes={handleConsolidateFromHeadcount}
              onCompletedChange={setDayOfHeadcountComplete}
            />
            {missedStopsReviewSection}
            {routesSection}
          </>
        ) : (
          <>
            <LineHaulSection
              event={EVENT_DEPARTURE}
              stockingDate={formatDateForApi(activeDate)}
              fulfillmentCenter={activeFc}
              market={marketValue}
              stepNumber={1}
              serverFields={prepLineHaulServer}
              hasServerAction={Boolean(prepLineHaulServer)}
              onConsolidateRoutes={scrollToRoutes}
              onCompletedChange={setPrepLineHaulComplete}
            />
            <HeadcountSection
              phase={HEADCOUNT_PHASE_PREP}
              stockingDate={formatDateForApi(activeDate)}
              fulfillmentCenter={activeFc}
              market={marketValue}
              routesPlanned={distinctRouteCount}
              stopsCount={filteredRoutes.length}
              routeNames={enrichedRouteGroups.map((g) => g.routeName)}
              stepNumber={2}
              disabled={!prepLineHaulComplete}
              disabledTooltip="Complete Line Haul Departure first"
              serverFields={prepHeadcountServer}
              hasServerAction={Boolean(prepHeadcountServer)}
              showConsolidateCta={!isLead}
              onConsolidateRoutes={handleConsolidateFromHeadcount}
              onCompletedChange={() => {}}
            />
            {missedStopsReviewSection}
            {routesSection}
          </>
        )}
      </div>

      {!isLead && (
        <ConsolidationShelf
          selectedRouteCount={consolidationSelection.size}
          onClear={() => setConsolidationSelection(new Set())}
          onConsolidate={handleConsolidateFromHeader}
          consolidateDisabled={isEditorLocked}
        />
      )}

      <ConsolidateSelectedModal
        show={consolidationModal.show}
        source={consolidationModal.source}
        selectedRouteGroups={enrichedRouteGroups
          .filter((g) => consolidationSelection.has(g.routeName))
          .map((g) => ({
            routeName: g.routeName,
            stops: g.stops.map((s) => ({
              ...s,
              displayName: nameByLocationId[s.id] || '',
            })),
          }))}
        availableDrivers={consolidationAvailability.availableDrivers}
        availableVans={consolidationAvailability.availableVans}
        reasonText={
          consolidationModal.source === 'headcount'
            ? 'Driver / Van shortage'
            : 'Manual consolidation'
        }
        onSubmit={handleConsolidationSubmit}
        onCancel={() =>
          setConsolidationModal({ show: false, source: 'manual' })
        }
      />
    </StyledDiv>
  );
}

RouteOperationsTab.propTypes = {
  region: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  market: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  fcByLocationId: PropTypes.objectOf(PropTypes.string),
  nameByLocationId: PropTypes.objectOf(PropTypes.string),
  coordsByLocationId: PropTypes.objectOf(
    PropTypes.shape({ lat: PropTypes.number, long: PropTypes.number }),
  ),
  refetchKey: PropTypes.number,
  onLoadingChange: PropTypes.func,
  boardRefreshKey: PropTypes.number,
  onBoardRefreshLoadingChange: PropTypes.func,
  onBoardRefreshed: PropTypes.func,
};

RouteOperationsTab.defaultProps = {
  fcByLocationId: {},
  nameByLocationId: {},
  coordsByLocationId: {},
  refetchKey: 0,
  onLoadingChange: () => {},
  boardRefreshKey: 0,
  onBoardRefreshLoadingChange: () => {},
  onBoardRefreshed: () => {},
};

export default RouteOperationsTab;
